import $ from 'jquery'
import { html, render } from 'lit-html'
import { debounce } from 'underscore'
import { bemBlock } from '@polleverywhere/bem-helpers'
import { cleanString } from '@polleverywhere/core/lib/util'

const bem = bemBlock('setup-saml-check')

function handleLoginClick (url, e) {
  e.preventDefault()
  window.location.href = url
}

function loginTemplate (login) {
  const title = `Log in with ${login.label}`

  return html`<button
    aria-label=${title}
    title=${title}
    @click=${handleLoginClick.bind(null, login.issuer)}
    class=${bem('login')}
  >${title}</button>`
}

// Updates the root block element with specified modifiers.
function updateBemBlock (hasLogins, forced) {
  this.classList.remove(...bem({ unforced: true, forced: true }).split(' '))
  this.classList.add(...bem({
    unforced: hasLogins && !forced,
    forced: hasLogins && forced
  }).split(' '))
}

function checkSaml (els) {
  return $.ajax({
    url: '/saml_check.json',
    data: {
      email_login: els.email.value
    }
  }).then((data) => {
    // Do nothing if signups are disabled.
    if (data.disable_signups) return

    const hasLogins = !!data.logins?.length
    const forced = data.force_saml

    // Render saml sso logins if they exist.
    render(data.logins?.map((login) => loginTemplate(login)), els.logins)
    updateBemBlock.call(this, hasLogins, forced)

    if (els.password) {
      els.password.disabled = hasLogins && forced
    }

    if (els.submit) {
      els.submit.disabled = hasLogins && forced
    }

    // Render password hint if it exists.
    if (els.passwordHint) {
      const passwordHintTemplate = hasLogins
        ? cleanString(data.password_reset_hint) : ''
      render(passwordHintTemplate, els.passwordHint)
    }
  }).catch((error) => {
    // TODO: Add some error handling here.
    console.error(error)
  })
}

/**
 * Augments an existing form containing an email field with a live async checker
 * that detects whether the user's email supports SAML single-sign-on. If
 * supported, login buttons are generated and inserted into a dedicated element
 * tagged with a special class name.
 *
 * Consumers must tag elements with the following special class names:
 *
 * - setup-saml-check__logins => Renders SAML logins if available
 * - setup-saml-check__email => Email form field
 * - setup-saml-check__password => (optional) Password form field
 * - setup-saml-check__submit => (optional) Submit form field
 * - setup-saml-check__password-hint => (optional) Renders password hints
 *
 * Consumers can tag elements with the following class names to auto-toggle them
 * to display based on if SAML logins are enabled and/or forced. Combinations
 * can be used to show an element for multiple states.
 *
 * - setup-saml-check__default => Shows when SAML logins are unavailable or off
 * - setup-saml-check__unforced => Shows when SAML is available and unforced
 * - setup-saml-check__forced => Shows when SAML is available and forced
 *
 * @param {Element} el HTML element container for this component.
 */
export default function setupSamlCheck (el) {
  const element = (name) => el.querySelector('.' + bem(name))
  const els = {
    logins: element('logins'),
    email: element('email'),
    password: element('password'),
    submit: element('submit'),
    passwordHint: element('password-hint')
  }

  // Ensure root bem block is tagged.
  el.classList.add(bem())

  // Trigger saml checks on every input to email field.
  els.email.addEventListener('input', debounce(checkSaml.bind(el, els), 400))

  // Check saml config on initial setup if email field is not blank.
  els.email.value && checkSaml.call(el, els)
}
